/** RadioStudio · /radio-studio · Architect-only upload console for the Sovereign Radio */
import React, { useCallback, useEffect, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK = 4 * 1024 * 1024;

export default function RadioStudio() {
  const [key, setKey] = useState(() => localStorage.getItem("nx_architect_key") || "");
  const [tracks, setTracks] = useState([]);
  const [stats, setStats] = useState(null);
  const [peaks, setPeaks] = useState([]);
  const [kind, setKind] = useState("music");
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileInput = useRef(null);
  const artInput = useRef(null);
  const artTarget = useRef(null);

  const authed = key.length > 3;

  const loadTracks = useCallback(async (k) => {
    const res = await fetch(`${API}/radio/tracks`, { headers: { "X-Architect-Key": k } });
    if (!res.ok) { setError("Key rejected — check your Architect Key."); return; }
    const data = await res.json();
    setError("");
    setTracks(data.tracks);
    setStats(data.stats);
    fetch(`${API}/radio/peaks`, { headers: { "X-Architect-Key": k } })
      .then((r) => (r.ok ? r.json() : { peaks: [] }))
      .then((d) => setPeaks((d.peaks || []).slice(0, 14).reverse()))
      .catch(() => {});
  }, []);

  useEffect(() => { if (authed) loadTracks(key); }, [authed, key, loadTracks]);

  const saveKey = (v) => {
    setKey(v);
    localStorage.setItem("nx_architect_key", v);
  };

  const uploadOne = async (file, fileKind, onProgress) => {
    const init = await fetch(`${API}/radio/upload/init`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Architect-Key": key },
      body: JSON.stringify({ kind: fileKind, filename: file.name, title: file.name.replace(/\.[^.]+$/, "") }),
    });
    if (!init.ok) throw new Error("init failed");
    const { upload_id } = await init.json();
    let sent = 0;
    while (sent < file.size) {
      const slice = file.slice(sent, sent + CHUNK);
      const fd = new FormData();
      fd.append("upload_id", upload_id);
      fd.append("chunk", slice, "chunk.bin");
      const r = await fetch(`${API}/radio/upload/chunk`, { method: "POST", headers: { "X-Architect-Key": key }, body: fd });
      if (!r.ok) throw new Error("chunk failed");
      sent += slice.size;
      onProgress(Math.round((sent / file.size) * 90));
    }
    const done = await fetch(`${API}/radio/upload/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Architect-Key": key },
      body: JSON.stringify({ upload_id }),
    });
    if (!done.ok) {
      const detail = await done.json().catch(() => ({}));
      throw new Error(detail.detail || "complete failed");
    }
    onProgress(100);
  };

  const startUpload = async (files) => {
    const list = [...files].map((f) => ({ file: f, name: f.name, progress: 0, state: "queued", kind }));
    setQueue(list);
    setBusy(true);
    for (const item of list) {
      item.state = "uploading";
      setQueue([...list]);
      try {
        await uploadOne(item.file, item.kind, (p) => { item.progress = p; setQueue([...list]); });
        item.state = "done";
      } catch (e) {
        item.state = `error: ${e.message}`;
      }
      setQueue([...list]);
    }
    setBusy(false);
    loadTracks(key);
  };

  const removeTrack = async (id) => {
    await fetch(`${API}/radio/tracks/${id}`, { method: "DELETE", headers: { "X-Architect-Key": key } });
    loadTracks(key);
  };

  const pickArt = (id) => { artTarget.current = id; artInput.current.click(); };

  const uploadArt = async (e) => {
    const f = e.target.files[0];
    if (!f || !artTarget.current) return;
    const fd = new FormData();
    fd.append("file", f);
    await fetch(`${API}/radio/tracks/${artTarget.current}/artwork`, {
      method: "POST", headers: { "X-Architect-Key": key }, body: fd,
    });
    e.target.value = "";
    loadTracks(key);
  };

  const gold = "#FFD700";
  return (
    <main className="min-h-screen px-4 py-10 max-w-[900px] mx-auto" data-testid="radio-studio-page">
      <h1 className="font-serif text-center mb-2" style={{ fontSize: "clamp(28px,4vw,40px)", color: gold, textShadow: "0 0 22px rgba(255,215,0,0.4)" }}>
        Radio Studio
      </h1>
      <p className="text-center font-mono uppercase tracking-[0.24em] mb-8" style={{ fontSize: 11, color: "#00FFCC" }}>
        Sovereign Pulse · Station Control
      </p>

      <section className="p-5 mb-6" style={{ background: "#111118", border: "1px solid rgba(255,215,0,0.4)", borderRadius: 14 }}>
        <label className="font-mono uppercase tracking-[0.2em] block mb-2" style={{ fontSize: 11, color: gold }}>
          Architect Key
        </label>
        <input
          type="password"
          data-testid="studio-key-input"
          value={key}
          onChange={(e) => saveKey(e.target.value)}
          placeholder="Enter your Architect Key…"
          className="w-full font-mono"
          style={{ minHeight: 44, padding: "0 14px", fontSize: 15, background: "rgba(4,5,10,0.7)", border: "1px solid rgba(0,255,204,0.4)", borderRadius: 8, color: "#e0e0e0" }}
        />
        {error && <p className="mt-2 font-mono" style={{ fontSize: 12, color: "#ff5470" }} data-testid="studio-error">{error}</p>}
      </section>

      {authed && !error && (
        <>
          {stats && (
            <section className="p-5 mb-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center" style={{ background: "#111118", border: "1px solid rgba(0,255,204,0.35)", borderRadius: 14 }} data-testid="studio-stats">
              {[
                ["Music Tracks", stats.music_count],
                ["Music Time", stats.music_hours >= 1 ? `${stats.music_hours}h` : `${Math.round(stats.music_hours * 60)}m`],
                ["Talk Shows", stats.talk_count],
                ["Talk Time", stats.talk_hours >= 1 ? `${stats.talk_hours}h` : `${Math.round(stats.talk_hours * 60)}m`],
                ["Jingles", stats.jingle_count ?? 0],
                ["Jingle Time", (stats.jingle_hours ?? 0) >= 1 ? `${stats.jingle_hours}h` : `${Math.round((stats.jingle_hours ?? 0) * 60)}m`],
              ].map(([label, val]) => (
                <div key={label}>
                  <p className="font-serif" style={{ fontSize: 26, color: gold }}>{val}</p>
                  <p className="font-mono uppercase tracking-[0.18em]" style={{ fontSize: 9, color: "rgba(224,224,224,0.55)" }}>{label}</p>
                </div>
              ))}
            </section>
          )}

          <section
            className="p-8 mb-6 text-center"
            data-testid="studio-dropzone"
            style={{ background: "#111118", border: "2px dashed rgba(255,215,0,0.45)", borderRadius: 14 }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (!busy) startUpload(e.dataTransfer.files); }}
          >
            <div className="flex items-center justify-center gap-4 mb-5 flex-wrap">
              {["music", "talk", "jingle"].map((k) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`studio-kind-${k}`}
                  onClick={() => setKind(k)}
                  className="font-mono uppercase tracking-[0.2em]"
                  style={{
                    minHeight: 44, padding: "0 22px", fontSize: 13, borderRadius: 999, cursor: "pointer",
                    color: kind === k ? "#0a0a0f" : gold,
                    background: kind === k ? "linear-gradient(135deg,#D4AF37,#FFD700)" : "transparent",
                    border: `1px solid ${gold}`,
                  }}
                >
                  {k === "music" ? "♪ Music" : k === "talk" ? "◉ Talk Show" : "♬ Jingle"}
                </button>
              ))}
            </div>
            <p className="font-serif mb-4" style={{ fontSize: 17, color: "#e0e0e0" }}>
              Drag &amp; drop MP3s here — they'll be seated as <strong style={{ color: gold }}>{kind === "music" ? "Music" : kind === "talk" ? "Talk Shows" : "Station Jingles"}</strong>
            </p>
            <button
              type="button"
              data-testid="studio-browse-btn"
              onClick={() => fileInput.current.click()}
              disabled={busy}
              className="font-mono uppercase tracking-[0.22em]"
              style={{ minHeight: 44, padding: "0 26px", fontSize: 13, color: "#0a0a0f", background: "linear-gradient(135deg,#00FFCC,#FFD700)", border: "none", borderRadius: 8, cursor: "pointer" }}
            >
              {busy ? "Uploading…" : "Choose Files"}
            </button>
            <input ref={fileInput} type="file" accept="audio/*" multiple hidden onChange={(e) => startUpload(e.target.files)} />
            {queue.length > 0 && (
              <div className="mt-6 text-left space-y-2" data-testid="studio-upload-queue">
                {queue.map((q) => (
                  <div key={q.name} className="font-mono" style={{ fontSize: 12, color: "#e0e0e0" }}>
                    <span style={{ color: q.state === "done" ? "#00FFCC" : q.state.startsWith("error") ? "#ff5470" : gold }}>
                      {q.state === "done" ? "✓" : q.state.startsWith("error") ? "✗" : "↑"}
                    </span>{" "}
                    {q.name} — {q.state.startsWith("error") ? q.state : `${q.progress}%`}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="p-5" style={{ background: "#111118", border: "1px solid rgba(255,215,0,0.3)", borderRadius: 14 }} data-testid="studio-track-list">
            <h2 className="font-mono uppercase tracking-[0.24em] mb-4" style={{ fontSize: 13, color: gold }}>
              Station Library ({tracks.length})
            </h2>
            {tracks.length === 0 && (
              <p className="font-mono" style={{ fontSize: 13, color: "rgba(224,224,224,0.5)" }}>No tracks yet — upload your first above.</p>
            )}
            {tracks.map((t) => (
              <div key={t.id} className="flex items-center gap-3 py-2" style={{ borderBottom: "1px dashed rgba(255,215,0,0.15)" }}>
                {t.artwork_path ? (
                  <img src={`${API}/radio/artwork/${t.id}`} alt="" style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 6, border: "1px solid rgba(255,215,0,0.4)" }} />
                ) : (
                  <span style={{ color: t.kind === "talk" ? "#00FFCC" : t.kind === "jingle" ? "#e0e0e0" : gold, fontSize: 14, width: 32, textAlign: "center" }}>
                    {t.kind === "talk" ? "◉" : t.kind === "jingle" ? "♬" : "♪"}
                  </span>
                )}
                <span className="font-serif flex-1" style={{ fontSize: 15, color: "#e0e0e0" }}>{t.title}</span>
                <span className="font-mono" style={{ fontSize: 11, color: "rgba(224,224,224,0.5)" }}>
                  {Math.floor(t.duration_sec / 60)}:{String(Math.round(t.duration_sec % 60)).padStart(2, "0")}
                </span>
                <button
                  type="button"
                  data-testid={`studio-art-${t.id}`}
                  onClick={() => pickArt(t.id)}
                  aria-label={`Upload artwork for ${t.title}`}
                  className="font-mono"
                  style={{ minHeight: 36, padding: "0 12px", fontSize: 11, color: "#00FFCC", background: "transparent", border: "1px solid rgba(0,255,204,0.4)", borderRadius: 6, cursor: "pointer" }}
                >
                  {t.artwork_path ? "✓ Art" : "+ Art"}
                </button>
                <button
                  type="button"
                  data-testid={`studio-delete-${t.id}`}
                  onClick={() => removeTrack(t.id)}
                  aria-label={`Remove ${t.title}`}
                  className="font-mono"
                  style={{ minHeight: 36, padding: "0 12px", fontSize: 11, color: "#ff5470", background: "transparent", border: "1px solid rgba(255,84,112,0.4)", borderRadius: 6, cursor: "pointer" }}
                >
                  Remove
                </button>
              </div>
            ))}
            <input ref={artInput} type="file" accept="image/*" hidden onChange={uploadArt} />
          </section>

          {peaks.length > 0 && (
            <section className="p-5 mt-6" style={{ background: "#111118", border: "1px solid rgba(0,255,204,0.3)", borderRadius: 14 }} data-testid="studio-peaks">
              <h2 className="font-mono uppercase tracking-[0.24em] mb-4" style={{ fontSize: 13, color: "#00FFCC" }}>
                Audience · Daily Peak Listeners
              </h2>
              <div className="flex items-end gap-2" style={{ height: 110 }}>
                {peaks.map((p) => {
                  const max = Math.max(...peaks.map((x) => x.peak), 1);
                  return (
                    <div key={p.date} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${p.date}: peak ${p.peak}`}>
                      <span className="font-mono" style={{ fontSize: 10, color: gold }}>{p.peak}</span>
                      <div style={{ width: "100%", maxWidth: 34, height: Math.max(4, (p.peak / max) * 70), background: "linear-gradient(180deg,#00FFCC,#D4AF37)", borderRadius: "4px 4px 0 0" }} />
                      <span className="font-mono" style={{ fontSize: 8, color: "rgba(224,224,224,0.45)" }}>{p.date.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}
    </main>
  );
}
